import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { FilesService } from '../../../../services/files.service';
import { RawFileInfo } from '../../../models/file-info';
import { PAGE_SIZE } from '../../../main.utilities';

export interface DirectorySubmitDialogResult {
  // A filename-matching regex (e.g. ".+\.ndpi") to narrow the run to one
  // file type, or null to run every supported image in the folder. The
  // backend matches this against the whole filename (jit-parent's
  // DirectoryExpansionService) - see buildOptions() for how an extension
  // choice here becomes one of these.
  regex: string | null;
}

export interface ExtensionFilterOption {
  label: string;
  value: string | null;
  count: number;
}

/**
 * Confirmation dialog shown before submitting a folder for processing
 * (jit-ui#131). Lists the folder's supported images grouped by extension,
 * lets the user narrow the run to one extension - or type their own
 * filename-matching regex, for directories mixing multiple resolutions or
 * image types that a simple extension filter can't separate - and reports
 * back the resulting regex (or null for "every image") when they submit.
 *
 * Opened imperatively via DialogService.open() rather than as a template
 * dialog, since ProcessComponentBase - the shared submission interception
 * point for every tool - is a template-less directive.
 */
@Component({
  selector: 'app-directory-submit-dialog',
  templateUrl: './directory-submit-dialog.component.html',
  styleUrls: ['./directory-submit-dialog.component.scss'],
})
export class DirectorySubmitDialogComponent implements OnInit {

  // Sentinel radio value for the free-text "regular expression on file
  // name" option, distinct from any real regex buildOptions() could ever
  // produce - selecting it switches selectedCount/confirm() over to
  // customRegex instead of an entry in `options`.
  readonly customRegexOptionValue = '__custom_regex__';

  loading = true;
  options: ExtensionFilterOption[] = [];
  selected: string | null = null;
  customRegex = '';

  // The same first-page-only fetch buildOptions() derives `options` from,
  // kept around so the custom-regex option can compute its own live count
  // as the user types, unfiltered by "supported image" - matching the
  // backend, where a regex/glob replaces that filter entirely rather than
  // narrowing it (jit-parent's DirectoryExpansionService.listImages).
  private allChildren: any[] = [];

  constructor(
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private filesService: FilesService,
  ) {}

  ngOnInit(): void {
    const folder: RawFileInfo = this.config.data.folder;
    // Same first-page-only scope as MainFacade.canProcessSelection$ - a
    // best-effort summary, not a guaranteed-exhaustive folder scan.
    this.filesService.getChildren(folder, 0, PAGE_SIZE).subscribe({
      next: (children: any[]) => {
        this.allChildren = children ?? [];
        this.options = this.buildOptions(this.allChildren);
        this.loading = false;
      },
      error: () => {
        this.allChildren = [];
        this.options = [];
        this.loading = false;
      },
    });
  }

  get selectedCount(): number {
    if (this.selected === this.customRegexOptionValue) {
      return this.customRegexCount;
    }
    return this.options.find((option) => option.value === this.selected)?.count ?? 0;
  }

  get customRegexValid(): boolean {
    return this.compileFullMatchRegex(this.customRegex) !== null;
  }

  get customRegexHasError(): boolean {
    return this.selected === this.customRegexOptionValue && !!this.customRegex && !this.customRegexValid;
  }

  get customRegexCount(): number {
    const pattern = this.compileFullMatchRegex(this.customRegex);
    if (!pattern) return 0;
    return this.allChildren.filter((child) => pattern.test(child?.name ?? '')).length;
  }

  cancel(): void {
    this.ref.close(null);
  }

  confirm(): void {
    const regex = this.selected === this.customRegexOptionValue ? this.customRegex : this.selected;
    const result: DirectorySubmitDialogResult = { regex };
    this.ref.close(result);
  }

  /**
   * Compiles `regex` the same way the backend applies it - matched against
   * the WHOLE filename (jit-parent's DirectoryExpansionService uses
   * Pattern.matcher(name).matches(), not find()) - so the count shown here
   * predicts server-side behavior exactly. Returns null for an empty or
   * invalid pattern.
   */
  private compileFullMatchRegex(regex: string): RegExp | null {
    if (!regex) return null;
    try {
      return new RegExp(`^(?:${regex})$`, 'i');
    } catch {
      return null;
    }
  }

  private buildOptions(children: any[]): ExtensionFilterOption[] {
    const images = children.filter((child) => child?.supportedImage);

    const countsByExtension = new Map<string, number>();
    images.forEach((image) => {
      const extension = this.extensionOf(image?.name ?? '');
      countsByExtension.set(extension, (countsByExtension.get(extension) ?? 0) + 1);
    });

    const allOption: ExtensionFilterOption = {
      label: `All images (${images.length})`,
      value: null,
      count: images.length,
    };

    const perExtensionOptions: ExtensionFilterOption[] = Array.from(countsByExtension.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([extension, count]) => ({
        // The "*.ext" label is just a familiar file-filter convention for
        // the user - what's actually submitted (regexForExtension) is a
        // regex the backend matches against the whole filename.
        label: `*.${extension} (${count})`,
        value: this.regexForExtension(extension),
        count,
      }));

    return [allOption, ...perExtensionOptions];
  }

  private extensionOf(name: string): string {
    const dotIndex = name.lastIndexOf('.');
    return dotIndex >= 0 ? name.slice(dotIndex + 1).toLowerCase() : '';
  }

  private regexForExtension(extension: string): string {
    return `.+\\.${this.escapeRegex(extension)}`;
  }

  private escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
